/**
 * StripeCheckoutForm - Custom Stripe checkout (dark theme, layout a lo nuestro)
 * Mobile-first; PaymentElement con dark + branding por variant; CTA gold para Exclusive Membership.
 */

import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Crown, Loader2 } from "lucide-react";
import { FormEvent, useState, useRef, useEffect, useMemo } from "react";

interface StripeCheckoutFormProps {
  /** Final amount to charge (cents). Debe coincidir con el PaymentIntent. */
  amount: number;
  currency: string;
  packName: string;
  packThumbnail?: string;
  language?: "en" | "es";
  /** Si hay descuento (ej. cupón EXCLUSIVO25), precio original en cents para mostrar tachado. */
  originalAmount?: number;
  /** Porcentaje de descuento aplicado (ej. 25) para mostrar "25% OFF". */
  discountPercent?: number;
  /**
   * Visual variant: "default" = purple/pink; "exclusive" = gold (Exclusive Membership).
   * El padre lo pasa cuando overridePackId === "exclusive".
   */
  variant?: "default" | "exclusive";
  /**
   * URL de retorno para métodos que requieren redirect (3DS, bancos).
   * Si no se pasa, usa /gracias o /thank-you según el idioma.
   */
  returnUrl?: string;
  /** Email pre-rellenado (si ya se capturó antes del checkout) */
  initialEmail?: string;
  /** Callback cuando el email cambia (para actualizar Customer antes del pago) */
  onEmailChange?: (email: string) => void;
  onSuccess: (paymentIntentId?: string, paymentIntentSecret?: string) => void;
  onError: (error: string) => void;
  onBack: () => void;
}

// Translations
const translations = {
  en: {
    back: "Back",
    checkout: "Checkout",
    exclusiveCheckout: "Exclusive Membership",
    fullAccess: "Full access to content",
    exclusiveFullAccess: "Private Channel + 1:1 chat with me",
    paymentInfo: "Payment information",
    emailLabel: "Email",
    emailPlaceholder: "your@email.com",
    emailRequired: "Email is required for your receipt",
    emailInvalid: "Please enter a valid email",
    processing: "Processing...",
    pay: "Pay",
    unlock: "Unlock Exclusive Membership",
    secureWith: "Secure payment with",
    processedBy: "Processed by",
    secureEncrypted: "100% secure and encrypted payment",
    discountApplied: "discount applied",
    firstMonthOff: "off first month",
    couponHint: "Coupon applied for your first month only",
  },
  es: {
    back: "Volver",
    checkout: "Checkout",
    exclusiveCheckout: "Membresía Exclusiva",
    fullAccess: "Acceso completo al contenido",
    exclusiveFullAccess: "Canal Privado + chat 1:1 conmigo",
    paymentInfo: "Información de pago",
    emailLabel: "Email",
    emailPlaceholder: "tu@email.com",
    emailRequired: "El email es necesario para tu recibo",
    emailInvalid: "Ingresa un email válido",
    processing: "Procesando...",
    pay: "Pagar",
    unlock: "Desbloquear Membresía Exclusiva",
    secureWith: "Pago seguro con",
    processedBy: "Procesado por",
    secureEncrypted: "Pago 100% seguro y encriptado",
    discountApplied: "descuento aplicado",
    firstMonthOff: "OFF el 1er mes",
    couponHint: "Cupón aplicado solo al primer mes",
  },
};

export function StripeCheckoutForm({
  amount,
  currency,
  packName,
  packThumbnail,
  originalAmount,
  discountPercent,
  language = "es",
  variant = "default",
  returnUrl,
  initialEmail = "",
  onEmailChange,
  onSuccess,
  onError,
  onBack,
}: StripeCheckoutFormProps) {
  const isExclusive = variant === "exclusive";
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);
  const errorRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  // Debug: Log when component mounts + detect browser
  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isAndroid = /android/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    console.log("[StripeCheckoutForm] Component mounted", {
      amount,
      currency,
      packName,
      hasStripe: !!stripe,
      hasElements: !!elements,
      browser: {
        isSafari,
        isAndroid,
        isIOS,
        userAgent: navigator.userAgent,
      },
    });
  }, []);

  // Debug: Log when Stripe/Elements are ready
  useEffect(() => {
    console.log("[StripeCheckoutForm] Stripe/Elements status", {
      stripe: !!stripe,
      elements: !!elements,
      buttonDisabled: !stripe || isProcessing,
    });
    
    // Safari iOS & Android: Scroll to button when Stripe is ready
    if (stripe && elements && buttonRef.current && !paymentElementReady) {
      setPaymentElementReady(true);
      // Small delay to ensure PaymentElement is fully rendered
      setTimeout(() => {
        if (buttonRef.current) {
          console.log("[StripeCheckoutForm] Scrolling to button for visibility");
          buttonRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }, 1000);
    }
  }, [stripe, elements, isProcessing, paymentElementReady]);

  useEffect(() => {
    if (errorMessage && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [errorMessage]);

  const t = translations[language];

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError(t.emailRequired);
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) {
      setEmailError(t.emailInvalid);
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailTouched) {
      validateEmail(value);
    }
    onEmailChange?.(value);
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    validateEmail(email);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    console.log("[StripeCheckoutForm] Form submitted", {
      hasStripe: !!stripe,
      hasElements: !!elements,
      email: email.trim(),
    });

    if (!stripe || !elements) {
      console.error("[StripeCheckoutForm] Stripe or Elements not ready");
      return;
    }

    // Validate email before submission
    setEmailTouched(true);
    if (!validateEmail(email)) {
      console.error("[StripeCheckoutForm] Email validation failed");
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // return_url: prop explícita (para BioTemplatePremium con success page propia)
      // o fallback a /gracias | /thank-you para el checkout genérico
      const thankYouPath = language === "es" ? "/gracias" : "/thank-you";
      const resolvedReturnUrl = returnUrl ?? `${window.location.origin}${thankYouPath}`;

      console.log("[StripeCheckoutForm] Confirming payment...", { returnUrl: resolvedReturnUrl });

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: resolvedReturnUrl,
          payment_method_data: {
            billing_details: {
              email: email.trim(),
            },
          },
          receipt_email: email.trim(),
        },
        redirect: "if_required",
      });

      if (error) {
        console.error("[StripeCheckoutForm] Payment error:", error);
        setErrorMessage(error.message || "Payment failed");
        onError(error.message || "Payment failed");
        setIsProcessing(false);
      } else if (paymentIntent) {
        console.log("[StripeCheckoutForm] Payment succeeded", { id: paymentIntent.id });
        // Payment succeeded without redirect
        // Pass payment intent details to parent for redirect
        onSuccess(paymentIntent.id, paymentIntent.client_secret);
      } else {
        console.log("[StripeCheckoutForm] Payment succeeded (redirect)");
        // Payment succeeded (will be handled by Stripe redirect)
        onSuccess();
      }
    } catch (err) {
      console.error("[StripeCheckoutForm] Unexpected error:", err);
      const message = err instanceof Error ? err.message : "An unexpected error occurred";
      setErrorMessage(message);
      onError(message);
      setIsProcessing(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  // Memoize formatted prices to avoid recalculating on every render
  const formattedAmount = useMemo(() => formatPrice(amount, currency), [amount, currency]);
  const formattedOriginalAmount = useMemo(
    () => originalAmount != null ? formatPrice(originalAmount, currency) : null,
    [originalAmount, currency]
  );

  // Memoize discount condition check
  const hasDiscount = useMemo(
    () => originalAmount != null && originalAmount > amount && discountPercent != null && discountPercent > 0,
    [originalAmount, amount, discountPercent]
  );

  const focusRing = isExclusive
    ? "focus-visible:ring-amber-400/70"
    : "focus-visible:ring-purple-500/60";

  return (
    <div
      className="w-full h-full flex flex-col bg-black text-white font-body"
      style={{
        WebkitOverflowScrolling: 'touch',
        transform: 'translateZ(0)',
      }}
    >
      {/* Header: sticky so it's always visible even when the page scrolls */}
      <div
        className={`sticky top-0 z-20 border-b flex-shrink-0 bg-black/95 backdrop-blur-md ${
          isExclusive ? "border-amber-500/20" : "border-white/10"
        }`}
        style={{
          WebkitBackfaceVisibility: 'hidden',
          paddingTop: 'max(0px, env(safe-area-inset-top, 0px))',
        }}
      >
        <div className="flex items-center justify-between px-4 py-2.5 gap-2">
          <button
            type="button"
            onClick={onBack}
            disabled={isProcessing}
            aria-label={t.back}
            className={`flex items-center gap-1.5 min-h-[44px] min-w-[44px] -ml-2 pl-2 pr-2 text-white/80 hover:text-white active:text-white/90 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 ${focusRing} focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-lg touch-manipulation`}
          >
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">{t.back}</span>
          </button>
          <h2 className="text-base font-semibold text-white font-display flex items-center gap-1.5 truncate">
            {isExclusive && (
              <Crown
                className="w-4 h-4 text-amber-400 flex-shrink-0"
                strokeWidth={2.5}
                aria-hidden
                style={{ filter: 'drop-shadow(0 0 6px rgba(251, 191, 36, 0.5))' }}
              />
            )}
            <span className="truncate">
              {isExclusive ? t.exclusiveCheckout : t.checkout}
            </span>
          </h2>
          <div className="w-16 flex-shrink-0" aria-hidden />
        </div>
        {/* Trust badge - Compacto */}
        <div className="flex items-center justify-center px-4 pb-2">
          <div className="flex items-center space-x-2 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1">
            <svg
              className="w-3 h-3 text-green-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-white/60 text-[10px] font-medium">
              {t.secureWith}
            </span>
            <svg
              className="h-3"
              viewBox="0 0 60 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z"
                fill="rgba(255, 255, 255, 0.7)"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Content - scroll natural, el botón siempre alcanzable */}
      <div 
        className="flex-1 overflow-y-auto overscroll-contain min-h-0"
        style={{
          // Safari iOS: Smooth scrolling
          WebkitOverflowScrolling: 'touch',
          // Android: Prevent scroll issues
          overscrollBehavior: 'contain',
        }}
      >
        <form
          onSubmit={handleSubmit}
          className="flex flex-col px-4 sm:px-5 py-3 sm:py-4 space-y-3 sm:space-y-4 max-w-lg mx-auto w-full pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))]"
        >
          {/* Pack summary - Compacto */}
          <div
            className={`rounded-2xl p-3 sm:p-4 transition-colors ${
              isExclusive
                ? "bg-gradient-to-br from-amber-500/[0.08] via-amber-400/[0.04] to-white/[0.02] border border-amber-500/30 ring-1 ring-amber-400/10"
                : "bg-white/[0.06] border border-white/10 ring-1 ring-transparent"
            }`}
          >
            <div className="flex items-center gap-3">
              {packThumbnail && (
                <div className="relative flex-shrink-0">
                  <img
                    src={packThumbnail}
                    alt={packName}
                    className={`w-12 h-12 sm:w-14 sm:h-14 rounded-lg object-cover ${
                      isExclusive ? "ring-1 ring-amber-400/40" : ""
                    }`}
                  />
                  {isExclusive && (
                    <div
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-md"
                      style={{
                        background: "linear-gradient(135deg, #fcd34d, #f59e0b)",
                      }}
                      aria-hidden
                    >
                      <Crown className="w-3 h-3 text-amber-950" strokeWidth={3} />
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <h3 className="text-white font-semibold text-sm sm:text-base font-display leading-tight truncate">
                    {packName}
                  </h3>
                  {isExclusive && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider text-amber-950 flex-shrink-0"
                      style={{
                        background: "linear-gradient(135deg, #fde68a, #f59e0b)",
                      }}
                    >
                      VIP
                    </span>
                  )}
                </div>
                <p
                  className={`text-xs mt-0.5 truncate ${
                    isExclusive ? "text-amber-100/70" : "text-white/60"
                  }`}
                >
                  {isExclusive ? t.exclusiveFullAccess : t.fullAccess}
                </p>
              </div>
              <div className="text-right flex-shrink-0 flex flex-col items-end gap-0.5">
                {hasDiscount ? (
                  <>
                    <span className="text-white/50 text-xs line-through font-medium">
                      {formattedOriginalAmount}
                    </span>
                    <span
                      className={`font-bold text-base sm:text-lg font-display ${
                        isExclusive ? "text-amber-200" : "text-white"
                      }`}
                    >
                      {formattedAmount}
                    </span>
                    <span
                      className={`text-[10px] font-semibold ${
                        isExclusive ? "text-amber-300" : "text-green-400"
                      }`}
                    >
                      {discountPercent}%{" "}
                      {isExclusive ? t.firstMonthOff : "OFF"}
                    </span>
                    {isExclusive && (
                      <span className="text-[9px] text-amber-200/70 font-medium max-w-[9rem] leading-tight">
                        {t.couponHint}
                      </span>
                    )}
                  </>
                ) : (
                  <span
                    className={`font-bold text-base sm:text-lg font-display ${
                      isExclusive ? "text-amber-200" : "text-white"
                    }`}
                  >
                    {formattedAmount}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Email field - Compacto */}
          <div className="space-y-1.5">
            <label
              htmlFor="checkout-email"
              className="block text-white/90 text-sm font-semibold"
            >
              {t.emailLabel}{" "}
              <span className={isExclusive ? "text-amber-400" : "text-purple-400"}>
                *
              </span>
            </label>
            <input
              id="checkout-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              placeholder={t.emailPlaceholder}
              className={`w-full bg-white/[0.06] border rounded-xl px-3 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:border-transparent transition-all font-body ${
                isExclusive
                  ? "focus:ring-amber-400/60"
                  : "focus:ring-purple-500/60"
              } ${
                emailError && emailTouched
                  ? "border-red-500/50 focus:ring-red-500/60"
                  : "border-white/15 hover:border-white/25"
              }`}
              style={{
                fontSize: '16px',
                WebkitTextSizeAdjust: '100%',
              }}
            />
            {emailError && emailTouched && (
              <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {emailError}
              </p>
            )}
          </div>

          {/* Payment information - altura natural, el PaymentElement crece según su contenido */}
          <div className="space-y-1.5">
            <label className="block text-white/90 text-sm font-semibold">
              {t.paymentInfo}
            </label>
            <div className="bg-white/[0.06] border border-white/15 rounded-xl p-3">
              <PaymentElement
                options={{
                  layout: {
                    type: "accordion",
                    defaultCollapsed: false,
                    radios: false,
                    spacedAccordionItems: false,
                  },
                  fields: {
                    billingDetails: {
                      email: "never",
                    },
                  },
                }}
              />
            </div>
          </div>

          {/* Error message */}
          {errorMessage && (
            <div
              ref={errorRef}
              role="alert"
              className="bg-red-500/10 border border-red-500/30 rounded-xl p-2.5"
            >
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-red-200 text-xs font-medium flex-1">
                  {errorMessage}
                </p>
              </div>
            </div>
          )}

          {/* CTA — always visible and reachable - OPTIMIZED FOR SAFARI iOS & ANDROID */}
          <div
            className="pt-3 pb-2"
            data-testid="payment-button-container"
            style={{
              position: 'relative',
              zIndex: 10,
              display: 'block',
              visibility: 'visible',
            }}
          >
            <button
              ref={buttonRef}
              type="submit"
              disabled={!stripe || isProcessing}
              aria-busy={isProcessing}
              aria-live="polite"
              data-testid="payment-submit-button"
              className={`group relative w-full font-bold py-3.5 px-6 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[56px] touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-black font-body overflow-hidden ${
                isExclusive
                  ? "text-amber-950 focus-visible:ring-amber-300"
                  : "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500 active:from-purple-700 active:to-pink-700 shadow-lg shadow-purple-900/30 focus-visible:ring-purple-400"
              }`}
              style={{
                fontSize: '17px',
                fontWeight: '800',
                letterSpacing: '0.01em',
                WebkitTapHighlightColor: 'transparent',
                WebkitAppearance: 'none',
                WebkitBackfaceVisibility: 'hidden',
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                transform: 'translateZ(0)',
                WebkitTransform: 'translateZ(0)',
                willChange: 'transform',
                visibility: 'visible',
                opacity: (!stripe || isProcessing) ? '0.6' : '1',
                ...(isExclusive
                  ? {
                      background:
                        'linear-gradient(135deg, #fde68a 0%, #fbbf24 35%, #f59e0b 65%, #b45309 100%)',
                      boxShadow:
                        '0 10px 30px -8px rgba(217, 119, 6, 0.65), 0 4px 10px -2px rgba(180, 83, 9, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.45), inset 0 -1px 0 rgba(120, 53, 15, 0.35)',
                    }
                  : {}),
              }}
            >
              {isExclusive && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-50"
                  style={{
                    background:
                      'linear-gradient(110deg, transparent 35%, rgba(255, 255, 255, 0.55) 50%, transparent 65%)',
                    transform: 'translateX(-100%)',
                    animation: 'gold-shine 3.6s ease-in-out infinite',
                  }}
                />
              )}
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin relative z-10" />
                  <span className="relative z-10">{t.processing}</span>
                </>
              ) : (
                <span className="relative z-10 flex items-center gap-2">
                  {isExclusive && (
                    <Crown
                      className="w-5 h-5 flex-shrink-0"
                      strokeWidth={2.5}
                      style={{ filter: 'drop-shadow(0 1px 0 rgba(120, 53, 15, 0.4))' }}
                    />
                  )}
                  <span>
                    {isExclusive ? t.unlock : `${t.pay} ${formattedAmount}`}
                  </span>
                  {isExclusive && (
                    <span className="font-display font-extrabold whitespace-nowrap">
                      · {formattedAmount}
                    </span>
                  )}
                </span>
              )}
            </button>
            {isExclusive && (
              <style>{`
                @keyframes gold-shine {
                  0% { transform: translateX(-100%); }
                  55% { transform: translateX(120%); }
                  100% { transform: translateX(120%); }
                }
              `}</style>
            )}
          </div>

          {/* Stripe Security Badge */}
          <div className="space-y-2 pb-2">
            {/* Powered by Stripe */}
            <div className="flex items-center justify-center space-x-1.5">
              <span className="text-white/40 text-[10px] font-medium">
                {t.processedBy}
              </span>
              <svg
                className="h-4"
                viewBox="0 0 60 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z"
                  fill="rgba(255, 255, 255, 0.5)"
                />
              </svg>
            </div>
            
            {/* Security notice */}
            <div className="flex items-center justify-center space-x-1.5 text-white/40 text-[10px] font-medium">
              <svg
                className="w-3 h-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{t.secureEncrypted}</span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
